import i18n from 'i18next';

/**
 * F7-12 — módulo ÚNICO de formatação de número, moeda, data e hora.
 *
 * Antes desta fatia o app tinha 96 ocorrências do literal `'pt-BR'` espalhadas
 * por ~60 arquivos: seis cópias de `formatCurrency`, duas de `formatNumber`,
 * e uma dúzia de `toLocaleDateString('pt-BR')` dentro de páginas. Quem trocava
 * o idioma continuava vendo `1.234,56` e `dd/mm/aaaa` — e, enquanto cada
 * página tiver a sua cópia, a próxima nasce com `'pt-BR'` de novo.
 *
 * ## Moeda NÃO é locale
 *
 * O erro que este módulo fecha e que a versão espalhada cometia (ver o
 * `CURRENCY_BY_LOCALE` que existia em `ExecutionDetails.tsx`, mapeando
 * en-US→USD e es-ES→EUR): trocar a MOEDA junto com o idioma. Um tenant que
 * cobra em BRL continua cobrando em BRL com a interface em inglês — mostrar
 * `$1.23` no lugar de `R$ 1,23` é um número errado na tela, não uma tradução.
 * Aqui o idioma decide só a FORMA (separador, posição do símbolo); a moeda vem
 * do dado, e o padrão é a moeda da plataforma.
 *
 * ## Por que funções puras e não só um hook
 *
 * Metade dos formatadores do app vive fora de componente (`types/*.ts`,
 * `services/*.ts`, `*-helpers.ts`), onde não dá para chamar hook. As funções
 * abaixo leem `i18n.language` na hora da chamada. Componente que precisa
 * RE-RENDERIZAR ao trocar de idioma usa `useFormatters()`, que depende do
 * `i18n.language` do `useTranslation` e refaz a memoização.
 */

export const LOCALES_SUPORTADOS = ['pt-BR', 'en-US', 'es-ES'] as const;
export type LocaleSuportado = (typeof LOCALES_SUPORTADOS)[number];

/** Fallback do projeto (ver `fallbackLng` em `locales/index.ts`). */
export const LOCALE_PADRAO: LocaleSuportado = 'pt-BR';

/**
 * Moeda da plataforma. É o padrão de quem não informa moeda — não é derivada
 * do idioma (ver o bloco "Moeda NÃO é locale" acima).
 */
export const MOEDA_PADRAO = 'BRL';

/**
 * O que sai no lugar de uma data que não dá para formatar. Um traço diz "não
 * há data"; `new Date('lixo').toLocaleDateString()` dizia "Invalid Date" na
 * cara do usuário, e `''` diria "está vazio", que é outra coisa.
 */
export const SEM_VALOR = '—';

const POR_PREFIXO: Record<string, LocaleSuportado> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES' };

/**
 * Idioma do i18next → locale de formatação.
 *
 * Aceita tanto a forma completa (`en-US`) quanto o prefixo (`en`): a versão
 * anterior deste mapa, em `ExecutionDetails.tsx`, só conhecia `en`/`es` — e
 * como o seletor de idioma grava `en-US`, ela caía no fallback e a formatação
 * por locale nunca acontecia de verdade.
 */
export function resolveLocale(language?: string | null): LocaleSuportado {
  if (!language) return LOCALE_PADRAO;
  const exato = LOCALES_SUPORTADOS.find((locale) => locale === language);
  if (exato) return exato;
  return POR_PREFIXO[language.split('-')[0].toLowerCase()] ?? LOCALE_PADRAO;
}

/** Locale de formatação ativo agora. */
export function localeAtivo(): LocaleSuportado {
  return resolveLocale(i18n.language);
}

type ComLocale<T> = T & { locale?: string };

export type EntradaDeData = Date | string | number;

function paraData(valor: EntradaDeData): Date | null {
  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

function separar<T>(options: ComLocale<T>): { locale: LocaleSuportado; resto: T } {
  const { locale, ...resto } = options as ComLocale<T> & { locale?: string };
  return { locale: locale ? resolveLocale(locale) : localeAtivo(), resto: resto as T };
}

export function formatNumber(value: number, options: ComLocale<Intl.NumberFormatOptions> = {}): string {
  const { locale, resto } = separar(options);
  return new Intl.NumberFormat(locale, resto).format(value);
}

/** `1234` → `1,2 mil` (pt-BR) / `1.2K` (en-US). Para eixo de gráfico e badge. */
export function formatCompactNumber(value: number, options: ComLocale<Intl.NumberFormatOptions> = {}): string {
  return formatNumber(value, { notation: 'compact', maximumFractionDigits: 1, ...options });
}

export interface OpcoesDeMoeda extends Intl.NumberFormatOptions {
  locale?: string;
  /** Código ISO da moeda do DADO. O idioma não o altera — só a forma. */
  currency?: string;
}

export function formatCurrency(value: number, options: OpcoesDeMoeda = {}): string {
  const { currency = MOEDA_PADRAO, ...resto } = options;
  return formatNumber(value, { style: 'currency', currency, ...resto });
}

/** Igual a `formatCurrency`, com o valor vindo em centavos. */
export function formatCurrencyFromCents(cents: number, options: OpcoesDeMoeda = {}): string {
  return formatCurrency(cents / 100, options);
}

export function formatCompactCurrency(value: number, options: OpcoesDeMoeda = {}): string {
  return formatCurrency(value, { notation: 'compact', maximumFractionDigits: 1, ...options });
}

function formatarData(
  valor: EntradaDeData | null | undefined,
  padrao: Intl.DateTimeFormatOptions,
  options: ComLocale<Intl.DateTimeFormatOptions>,
): string {
  if (valor === null || valor === undefined || valor === '') return SEM_VALOR;
  const data = paraData(valor);
  if (!data) return SEM_VALOR;
  const { locale, resto } = separar(options);
  return new Intl.DateTimeFormat(locale, { ...padrao, ...resto }).format(data);
}

/**
 * Só a data: `31/12/2026` (pt-BR/es-ES) / `12/31/2026` (en-US).
 *
 * Partes explícitas em vez de `dateStyle: 'short'` de propósito: o `short` do
 * en-US corta o ano para dois dígitos (`12/31/26`), e o app inteiro exibia
 * `dd/mm/aaaa` via `toLocaleDateString('pt-BR')`. Assim a ORDEM acompanha o
 * idioma sem que a largura do ano mude de tela para tela.
 */
export const PARTES_DE_DATA: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export function formatDate(
  valor: EntradaDeData | null | undefined,
  options: ComLocale<Intl.DateTimeFormatOptions> = {},
): string {
  return formatarData(valor, PARTES_DE_DATA, options);
}

/** Só a hora: `14:05` nos três locales (en-US inclui AM/PM). */
export function formatTime(
  valor: EntradaDeData | null | undefined,
  options: ComLocale<Intl.DateTimeFormatOptions> = {},
): string {
  return formatarData(valor, { hour: '2-digit', minute: '2-digit' }, options);
}

/** Data e hora juntas, com a mesma escolha de partes de `formatDate`. */
export function formatDateTime(
  valor: EntradaDeData | null | undefined,
  options: ComLocale<Intl.DateTimeFormatOptions> = {},
): string {
  return formatarData(valor, { ...PARTES_DE_DATA, hour: '2-digit', minute: '2-digit' }, options);
}

export interface Formatadores {
  locale: LocaleSuportado;
  formatNumber: (value: number, options?: ComLocale<Intl.NumberFormatOptions>) => string;
  formatCompactNumber: (value: number, options?: ComLocale<Intl.NumberFormatOptions>) => string;
  formatCurrency: (value: number, options?: OpcoesDeMoeda) => string;
  formatCurrencyFromCents: (cents: number, options?: OpcoesDeMoeda) => string;
  formatCompactCurrency: (value: number, options?: OpcoesDeMoeda) => string;
  formatDate: (valor: EntradaDeData | null | undefined, options?: ComLocale<Intl.DateTimeFormatOptions>) => string;
  formatTime: (valor: EntradaDeData | null | undefined, options?: ComLocale<Intl.DateTimeFormatOptions>) => string;
  formatDateTime: (valor: EntradaDeData | null | undefined, options?: ComLocale<Intl.DateTimeFormatOptions>) => string;
}

/**
 * Fixa o locale ativo num conjunto de formatadores. Usado por `useFormatters`
 * para que o componente re-renderize com a formatação nova ao trocar o idioma.
 */
export function criarFormatadores(language?: string | null): Formatadores {
  const locale = resolveLocale(language);
  return {
    locale,
    formatNumber: (value, options = {}) => formatNumber(value, { locale, ...options }),
    formatCompactNumber: (value, options = {}) => formatCompactNumber(value, { locale, ...options }),
    formatCurrency: (value, options = {}) => formatCurrency(value, { locale, ...options }),
    formatCurrencyFromCents: (cents, options = {}) => formatCurrencyFromCents(cents, { locale, ...options }),
    formatCompactCurrency: (value, options = {}) => formatCompactCurrency(value, { locale, ...options }),
    formatDate: (valor, options = {}) => formatDate(valor, { locale, ...options }),
    formatTime: (valor, options = {}) => formatTime(valor, { locale, ...options }),
    formatDateTime: (valor, options = {}) => formatDateTime(valor, { locale, ...options }),
  };
}
